import { Component, inject } from '@angular/core';

import { finalize } from 'rxjs';

import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Composants
import { PageBaseComponent } from 'src/app/@shared/components/abstract/page-base.component';
import { MessageAction, MessageComponent } from 'src/app/@shared/components/message/message.component';

// Services
import { AuthenticationService } from 'src/app/@core/authentication/services/authentication.service';

// Utils
import { ODataMessage } from 'src/app/@shared/utilities/odata/message';

@Component({
    selector: 'app-verify-user',
    imports: [
        MessageComponent,
        ProgressSpinnerModule,
    ],
    templateUrl: './verify-user.component.html',
    styleUrls: ['./verify-user.component.scss']
})
export class VerifyUserComponent extends PageBaseComponent {
    private _authenticationService = inject(AuthenticationService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    message: string = '';

    /**
     * Motif du refus, tel que le serveur le nomme (#1111) : `REGISTRATION_LINK_EXPIRED`,
     * `REGISTRATION_LINK_INVALID`, `REGISTRATION_LINK_INCOMPLETE` ou
     * `REGISTRATION_NOT_FOUND`. On affiche le texte du référentiel sans le recopier ici —
     * `REGISTRATION_NOT_FOUND` finirait sinon présenté comme une expiration, alors que le
     * lien y est le plus souvent encore valide : seul le compte a été purgé.
     */
    public refusal: ODataMessage | null = null;

    /**
     * Repli quand le refus n'est pas nommé : réponse sans `messages` (erreur serveur,
     * coupure réseau). L'écran ne doit jamais rester nu — c'est le symptôme d'origine du
     * ticket, sept clics en quatre-vingt-dix secondes devant un écran muet.
     */
    private readonly _unknownRefusal: ODataMessage = new ODataMessage({
        domain: 'registration',
        longText: `La validation de votre compte n'a pas abouti. Rouvrez le lien reçu par email dans quelques `
            + `instants, ou reprenez votre inscription pour en recevoir un nouveau.`,
        shortText: 'Validation impossible',
        type: 'E'
    });

    /** Sortie commune aux quatre refus : le formulaire d'inscription renvoie un lien neuf. */
    public readonly restartAction: MessageAction = {
        label: `Reprendre mon inscription`,
        handler: () => this._restartRegistration(),
    };

    public readonly loginActions: MessageAction[] = [{
        label: `Revenir à la connexion`,
        handler: () => this._goToLogin(),
    }];

    override ngOnInit(): void {
        super.ngOnInit();

        this._verifyUser();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _goToLogin(): void {
        this.router.navigate(['/login']);
    }

    private _restartRegistration(): void {
        this.router.navigate(['/subscribe']);
    }

    private _verifyUser(): void {
        let payload = {
            'user_id': this.queryParams.get('user_id'),
            'timestamp': this.queryParams.get('timestamp'),
            'signature': this.queryParams.get('signature')
        };

        this.loading = true;
        this.refusal = null;

        this._authenticationService.verifyUser$(payload)
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .subscribe({
                next: () => {
                    this.message = `Votre compte a été validé, vous pouvez maintenant vous connecter. Vous allez être redirigé vers l'écran de connexion dans 5 secondes.`;
                    setTimeout(() => {
                        this.router.navigate(['/login'])
                    }, 5000);
                },
                error: (error: any) => {
                    this.refusal = error?.messages?.[0] ?? this._unknownRefusal;
                },
            });
    }

}
